import { useState, useEffect, memo } from 'react';
import type { FC, JSX } from 'react';
import crmApi from 'src/api/CrmApi';
import { Checkbox, FormGroup, Box, Button, Divider } from '@mui/material';
import { Activity } from 'src/types/activity';
import { CRMActivityEntity } from 'src/types/CRMActivityEntity';
import moment from 'moment';
import useSnackbar from 'src/hooks/useSnackbar';
import useNotification from 'src/hooks/useNotifications';
import { getPastDueTasks, getTasksDueToday, getUpcomingTasks } from './NotificationsLogic';
import 'src/theme/partials/notifications/NotificationsBase.css';
import useCrm from 'src/hooks/useCrm';
import AttachFileIcon from '@mui/icons-material/AttachFile';

interface NotificationsBodyProps {
  showUpcomingTasks?: boolean;
  children?: React.ReactNode;
}

const NotificationsBody: FC<NotificationsBodyProps> = ({ showUpcomingTasks = false }) => {
  const { tasksInNotificationRange, reloadTaskNotifications } = useNotification();
  const { editActivityClicked } = useCrm();
  const [tasksDueToday, setTasksDueToday] = useState<Activity[]>([]);
  const [pastDueTasks, setPastDueTasks] = useState<Activity[]>([]);
  const [upcomingTasks, setUpcomingTasks] = useState<Activity[]>([]);
  const { openSnackBar } = useSnackbar();

  useEffect(() => {
    const dueToday = getTasksDueToday(tasksInNotificationRange);
    const pastDue = getPastDueTasks(tasksInNotificationRange);
    setTasksDueToday(dueToday);
    setPastDueTasks(pastDue);
    setUpcomingTasks(getUpcomingTasks(tasksInNotificationRange));
  }, [tasksInNotificationRange]);

  // Check tasks complete
  const toggleTaskComplete = (activity: Activity) => {
    crmApi
      .getCrmActivity(activity.activityId)
      .then((crmActivity: CRMActivityEntity) => {
        crmActivity.isComplete = !crmActivity.isComplete;
        crmApi.upsertActivity(crmActivity).then((res) => {
          openSnackBar(`Completed, ${res.title}`, 'success', 1500);
          reloadTaskNotifications();
        });
      })
      .catch((err) => {
        console.error('Error: ', err);
      });
  };

  const handleEditClicked = (activity: Activity) => {
    editActivityClicked(activity.activityId);
  };

  const renderContent = (content: string, trunkLen: number) => {
    if (content.length > trunkLen) {
      return content.substring(0, trunkLen) + '...';
    }
    return content;
  };

  //The row of the task in modal and notification drawer
  const TaskRow: FC<{ tasks: Activity[] }> = ({ tasks }) => (
    <FormGroup>
      {tasks.map((task) => {
        let title = task.title;

        // Break apart by words
        let titleWords: (string | JSX.Element)[] = title.split(' ');

        // Transform words: if they start with http or https, make them a link
        titleWords = titleWords.map((word, index) => {
          if (typeof word === 'string' && (word.startsWith('http') || word.startsWith('https'))) {
            return (
              <a key={index} href={word} target="_blank" rel="noopener noreferrer">
                {word}
              </a>
            );
          }
          return word;
        });

        return (
          <div key={task.activityId} className="notifications__check-container">
            <Checkbox title="Mark task as completed and save" onChange={() => toggleTaskComplete(task)} />
            <span className="notifications__title">
              {titleWords.map((word, index) => (
                <span key={index}>{word} </span>
              ))}{' '}
              ({moment(task.dueDate).format('MMM D, YY')})
            </span>
            &nbsp; |&nbsp;
            <Button title="Pop up the form to edit the task" sx={{ px: '8px' }} onClick={() => handleEditClicked(task)} variant="text" size="small">
              <strong>Edit</strong>
            </Button>
            {task.content && (
              <Box sx={{ px: '8px' }} className="notifications__content">
                <div dangerouslySetInnerHTML={{ __html: renderContent(task.content, 500) }} />
                <Divider />
              </Box>
            )}
            {task.totalAttachments > 0 && (
              <Box sx={{ p: '8px', display: 'flex', alignItems: 'center' }}>
                <AttachFileIcon fontSize="small" />
                <em>
                  {task.totalAttachments} {task.totalAttachments === 1 ? 'Attachment' : 'Attachments'}
                </em>
              </Box>
            )}
          </div>
        );
      })}
    </FormGroup>
  );

  return (
    <>
      <Box sx={{ maxWidth: '400px' }}>
        <>
          {tasksDueToday.length > 0 && (
            <div className="notifications-body__check-group due-today">
              <p>Due Today</p>
              <TaskRow tasks={tasksDueToday} />
            </div>
          )}

          {pastDueTasks.length > 0 && (
            <div className="notifications-body__check-group past-due">
              <p>Past Due</p>
              <TaskRow tasks={pastDueTasks} />
            </div>
          )}

          {showUpcomingTasks && upcomingTasks.length > 0 && (
            <div className="notifications-body__check-group upcoming">
              <p>Upcoming</p>
              <TaskRow tasks={upcomingTasks} />
            </div>
          )}

          {tasksDueToday.length === 0 && pastDueTasks.length === 0 && upcomingTasks.length === 0 && (
            <div className="notifications-body__check-group no-tasks">
              <p>All caught up!</p>
            </div>
          )}
        </>
      </Box>
    </>
  );
};

export default memo(NotificationsBody);
